'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { ArrowDown, Zap, Sun, Leaf } from 'lucide-react';

interface HeroSectionMobileProps {
  title: string;
  subtitle: string;
  stats: {
    value: number;
    label: string;
    icon: React.ReactNode;
  }[];
  onScrollDown: () => void;
  gemeindeName?: string;
  wappenLink?: string;
}

export function HeroSectionMobile({
  title,
  subtitle,
  stats,
  onScrollDown,
  gemeindeName,
  wappenLink,
}: HeroSectionMobileProps) {

  return (
    <section className="relative w-full min-h-screen flex flex-col text-white overflow-hidden">
      {/* Animated Gradient Background - Same as desktop */}
      <motion.div 
        className="absolute inset-0 z-0"
        animate={{
          background: [
            'linear-gradient(to bottom right, #064e3b, #065f46, #047857)',
            'linear-gradient(to bottom right, #047857, #059669, #10b981)',
            'linear-gradient(to bottom right, #065f46, #047857, #064e3b)',
            'linear-gradient(to bottom right, #064e3b, #065f46, #047857)',
          ],
        }}
        transition={{
          duration: 10,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        }}
      />
      
      {/* Grid overlay - Same as desktop but lighter */}
      <div className="absolute inset-0 bg-grid-white/[0.03] [mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,0.8))]"></div>
      
      {/* Compact Header */}
      <div className="relative z-10 pt-safe">
        <div className="px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-center py-4"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
              <Image 
                src="/logo-white.svg" 
                alt="Bürgerstrom Tirol" 
                width={200} 
                height={40}
                className="h-6 w-auto"
                priority
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content - Compact spacing */}
      <div className="relative z-10 flex-1 flex flex-col justify-between px-4 pb-6">
        {/* Title Section - Much more compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-center mt-4"
        >
          {gemeindeName ? (
            <div className="mb-4">
              <h1 className="text-2xl font-bold leading-tight mb-3">
                Energiezukunft in
              </h1>
              <div className="flex items-center justify-center gap-3">
                {wappenLink && (
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-xl border border-white/30 p-2 shadow-lg flex-shrink-0"
                  >
                    <img 
                      src={wappenLink} 
                      alt={`Wappen ${gemeindeName}`}
                      className="w-full h-full object-contain"
                    />
                  </motion.div>
                )}
                <span className="text-yellow-300 text-3xl font-bold">{gemeindeName}</span>
              </div>
            </div>
          ) : (
            <h1 className="text-3xl font-bold leading-tight mb-3">{title}</h1>
          )}
          <p className="text-sm text-white/80 max-w-xs mx-auto">
            {subtitle}
          </p>
        </motion.div>

        {/* Stats - Compact 2x2 grid */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="my-8"
        >
          <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
            {stats.slice(0, 4).map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 mb-3 mx-auto">
                  {stat.icon}
                </div>
                <div className="text-xl font-bold leading-tight">
                  {stat.value === 0 ? '-' : new Intl.NumberFormat('de-DE', { 
                    notation: stat.value > 999999 ? 'compact' : 'standard',
                    compactDisplay: 'short',
                    maximumFractionDigits: stat.value > 999999 ? 1 : 0 
                  }).format(stat.value)}
                  {stat.label.includes('€') && stat.value > 0 ? '€' : ''}
                </div>
                <div className="text-xs text-white/70 leading-tight mt-1">
                  {stat.label.replace('Jahrespotenzial', '').replace('pro Jahr', '').replace('(€)', '').trim()}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section - Prominent and accessible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="space-y-4"
        >
          {/* Primary CTA Button - High contrast for visibility */}
          <button
            onClick={onScrollDown}
            className="w-full bg-white text-gray-900 font-bold py-4 px-6 rounded-2xl shadow-xl transform transition-all hover:scale-105 active:scale-95 relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 opacity-0 group-hover:opacity-10 transition-opacity" />
            <span className="relative flex items-center justify-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-green-600" />
              Jetzt Potenzial entdecken
            </span>
          </button>
          
          {/* Secondary scroll indicator */}
          <div className="flex justify-center">
            <motion.div
              animate={{
                y: [0, 5, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="text-white/50"
            >
              <ArrowDown className="w-5 h-5" />
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Mobile-optimized floating icons - 2 subtle elements */}
      <motion.div 
        className="absolute top-20 right-8 pointer-events-none"
        animate={{
          y: [0, -30, 0],
          opacity: [0.2, 0.3, 0.2],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <Sun className="w-10 h-10 text-yellow-300/30" />
      </motion.div>
      
      <motion.div 
        className="absolute bottom-32 left-8 pointer-events-none"
        animate={{
          y: [0, -25, 0],
          opacity: [0.2, 0.3, 0.2],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      >
        <Leaf className="w-8 h-8 text-green-300/30" />
      </motion.div>
    </section>
  );
}